import { useState, useEffect, useCallback } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { PageLoader, LoadingSpinner } from '../components/LoadingSpinner';
import { dbSelect, dbUpdate } from '../lib/api';
import {
    Folders,
    Star,
    StarOff,
    ExternalLink,
    User,
    RefreshCw,
    Archive,
    ArchiveRestore,
} from 'lucide-react';
import { MiniAppType } from '../lib/supabase';

interface PublicProjectForAdmin {
    id: string;
    name: string;
    description: string | null;
    type: MiniAppType | null;
    admin_type_override: MiniAppType | null;
    slug: string | null;
    public_id: string | null;
    is_promoted: boolean;
    created_at: string;
    user_id: string;
    profiles: {
        email: string;
        display_name: string | null;
        is_admin: boolean;
    } | null;
    is_skipped_for_promotion?: boolean;
}

export function AdminPromotePage() {
    const { user, session, loading: authLoading } = useAuth();

    // Project promotion state
    const [publicProjects, setPublicProjects] = useState<PublicProjectForAdmin[]>([]);
    const [viewMode, setViewMode] = useState<'active' | 'skipped'>('active');
    const [projectsLoading, setProjectsLoading] = useState(true);
    const [promotingProjectId, setPromotingProjectId] = useState<string | null>(null);
    const [updatingProjectTypeId, setUpdatingProjectTypeId] = useState<string | null>(null);
    const [skippingProjectId, setSkippingProjectId] = useState<string | null>(null);

    // Hardcoded admin email check as per requirement
    const ADMIN_EMAIL = 'a.burkov@gmail.com';

    useEffect(() => {
        document.title = 'Promote Mini-Apps - Llambada';
        return () => { document.title = 'Llambada'; };
    }, []);

    const fetchPublicProjects = useCallback(async () => {
        if (!session) return;

        setProjectsLoading(true);
        try {
            // Fetch all public projects by non-admin users
            const { data, error } = await dbSelect<PublicProjectForAdmin[]>('projects', {
                token: session.access_token,
                select: 'id,name,description,type,admin_type_override,slug,public_id,is_promoted,created_at,user_id,is_skipped_for_promotion,profiles!projects_user_id_fkey(email,display_name,is_admin)',
                filters: { is_published: 'eq.true', public_id: 'not.is.null' },
                order: 'created_at.desc',
            });

            if (error) throw error;

            // Transform data: handle profiles being returned as array from Supabase join
            const transformedProjects: PublicProjectForAdmin[] = (data || []).map((project) => ({
                ...project,
                profiles: Array.isArray((project as any).profiles)
                    ? (project as any).profiles[0] || null
                    : (project as any).profiles,
            }));

            // Filter to only show non-admin user projects
            const nonAdminProjects = transformedProjects.filter(
                (project) => project.profiles?.is_admin !== true
            );

            setPublicProjects(nonAdminProjects);
        } catch (err) {
            console.error('Failed to fetch public projects:', err);
        } finally {
            setProjectsLoading(false);
        }
    }, [session]);

    useEffect(() => {
        if (user?.email === ADMIN_EMAIL) {
            fetchPublicProjects();
        }
    }, [user, fetchPublicProjects, ADMIN_EMAIL]);

    const handleTogglePromotion = async (projectId: string, currentlyPromoted: boolean) => {
        if (!session) return;

        setPromotingProjectId(projectId);
        try {
            const { error } = await dbUpdate(
                'projects',
                { is_promoted: !currentlyPromoted },
                { id: `eq.${projectId}` },
                { token: session.access_token, returning: false }
            );

            if (error) throw error;

            // Update local state
            setPublicProjects((prev) =>
                prev.map((p) =>
                    p.id === projectId ? { ...p, is_promoted: !currentlyPromoted } : p
                )
            );
        } catch (err) {
            console.error('Failed to update promotion status:', err);
        } finally {
            setPromotingProjectId(null);
        }
    };

    const handleUpdateProjectType = async (projectId: string, overrideType: MiniAppType | null) => {
        if (!session) return;
        setUpdatingProjectTypeId(projectId);
        try {
            const { data, error } = await dbUpdate<PublicProjectForAdmin>(
                'projects',
                { admin_type_override: overrideType },
                { id: `eq.${projectId}` },
                { token: session.access_token }
            );

            if (error) throw error;

            if (data) {
                setPublicProjects((prev) =>
                    prev.map((project) => (project.id === projectId ? { ...project, ...data } : project))
                );
            }
        } catch (err) {
            console.error('Failed to update project type override:', err);
        } finally {
            setUpdatingProjectTypeId(null);
        }
    };

    const handleToggleSkip = async (projectId: string, skip: boolean) => {
        if (!session) return;

        setSkippingProjectId(projectId);
        try {
            const { error } = await dbUpdate(
                'projects',
                { is_skipped_for_promotion: skip },
                { id: `eq.${projectId}` },
                { token: session.access_token, returning: false }
            );

            if (error) throw error;

            // Update local state
            setPublicProjects((prev) =>
                prev.map((p) =>
                    p.id === projectId ? { ...p, is_skipped_for_promotion: skip } : p
                )
            );
        } catch (err) {
            console.error('Failed to update skip status:', err);
        } finally {
            setSkippingProjectId(null);
        }
    };

    const getProjectUrl = (project: PublicProjectForAdmin) => {
        return `/p/${project.public_id}${project.slug ? `/${project.slug}` : ''}`;
    };

    if (authLoading) return <PageLoader />;

    if (!user || user.email !== ADMIN_EMAIL) {
        return <Navigate to="/dashboard" replace />;
    }

    return (
        <Layout>
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex items-center gap-3 mb-8">
                    <Link to="/admin" className="btn btn-ghost btn-sm -ml-2">
                        ← Back
                    </Link>
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-500/10 text-amber-500">
                        <Star className="h-6 w-6" />
                    </div>
                    <div>
                        <h1 className="font-display text-2xl font-bold text-theme-text">
                            Promote Public Mini-Apps
                        </h1>
                        <p className="text-theme-text-secondary">
                            Manage which user-created public mini-apps appear on the home page. Only promoted mini-apps from non-admin users will be visible.
                        </p>
                    </div>
                </div>

                <div className="card p-6 mb-8">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-2">
                            <h2 className="text-lg font-semibold text-theme-text">All Public Mini-Apps</h2>
                            <span className="text-sm text-theme-text-muted">({publicProjects.length})</span>
                        </div>
                        <button
                            onClick={fetchPublicProjects}
                            disabled={projectsLoading}
                            className="btn btn-secondary btn-sm"
                        >
                            <RefreshCw className={`h-4 w-4 ${projectsLoading ? 'animate-spin' : ''}`} />
                            Refresh
                        </button>
                    </div>

                    <div className="flex gap-4 border-b border-theme-border-muted mb-6">
                        <button
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${viewMode === 'active'
                                ? 'border-theme-primary text-theme-primary'
                                : 'border-transparent text-theme-text-muted hover:text-theme-text'
                                }`}
                            onClick={() => setViewMode('active')}
                        >
                            Active ({publicProjects.filter(p => !p.is_skipped_for_promotion).length})
                        </button>
                        <button
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${viewMode === 'skipped'
                                ? 'border-theme-primary text-theme-primary'
                                : 'border-transparent text-theme-text-muted hover:text-theme-text'
                                }`}
                            onClick={() => setViewMode('skipped')}
                        >
                            Skipped ({publicProjects.filter(p => p.is_skipped_for_promotion).length})
                        </button>
                    </div>

                    {projectsLoading ? (
                        <div className="flex justify-center py-8">
                            <LoadingSpinner size="md" />
                        </div>
                    ) : publicProjects.length === 0 ? (
                        <div className="text-center py-8">
                            <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-theme-surface mb-4">
                                <Folders className="h-6 w-6 text-theme-text-muted" />
                            </div>
                            <p className="text-theme-text-muted">
                                No public projects from non-admin users yet.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {publicProjects
                                .filter((p) => viewMode === 'active' ? !p.is_skipped_for_promotion : p.is_skipped_for_promotion)
                                .map((project) => (
                                    <div
                                        key={project.id}
                                        className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${project.is_promoted
                                            ? 'bg-amber-500/5 border-amber-500/30'
                                            : 'bg-theme-surface/50 border-theme-border-muted'
                                            }`}
                                    >
                                        <div className="flex items-center gap-4 min-w-0 flex-1">
                                            <div className={`flex h-10 w-10 items-center justify-center rounded-lg shrink-0 ${project.is_promoted
                                                ? 'bg-amber-500/20'
                                                : 'bg-theme-surface'
                                                }`}>
                                                <Folders className={`h-5 w-5 ${project.is_promoted ? 'text-amber-500' : 'text-theme-text-muted'
                                                    }`} />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2">
                                                    <h3 className="font-medium text-theme-text truncate">
                                                        {project.name}
                                                    </h3>
                                                    {project.is_promoted && (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/20 text-amber-600 dark:text-amber-400">
                                                            <Star className="h-3 w-3" />
                                                            Promoted
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-3 mt-1 text-sm text-theme-text-muted">
                                                    <span className="flex items-center gap-1">
                                                        <User className="h-3.5 w-3.5" />
                                                        {project.profiles?.display_name || project.profiles?.email || 'Unknown'}
                                                    </span>
                                                    {project.description && (
                                                        <span className="truncate max-w-xs">
                                                            {project.description}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0 ml-4">
                                            <div className="flex items-center gap-2">
                                                <label className="text-xs text-theme-text-muted">Type</label>
                                                <select
                                                    className="input py-1.5 px-2 text-sm"
                                                    value={project.admin_type_override ?? ''}
                                                    onChange={(e) => {
                                                        const value = e.target.value as MiniAppType | '';
                                                        handleUpdateProjectType(project.id, value === '' ? null : value);
                                                    }}
                                                    disabled={updatingProjectTypeId === project.id}
                                                >
                                                    <option value="">User choice ({project.type ?? 'utility'})</option>
                                                    <option value="game">Game</option>
                                                    <option value="utility">Utility</option>
                                                </select>
                                            </div>
                                            <Link
                                                to={getProjectUrl(project)}
                                                target="_blank"
                                                className="btn btn-secondary btn-sm"
                                            >
                                                <ExternalLink className="h-4 w-4" />
                                                View
                                            </Link>
                                            <button
                                                onClick={() => handleTogglePromotion(project.id, project.is_promoted)}
                                                disabled={promotingProjectId === project.id}
                                                className={`btn btn-sm ${project.is_promoted
                                                    ? 'btn-secondary'
                                                    : 'bg-amber-500 hover:bg-amber-600 text-white'
                                                    }`}
                                            >
                                                {promotingProjectId === project.id ? (
                                                    <LoadingSpinner size="sm" />
                                                ) : project.is_promoted ? (
                                                    <>
                                                        <StarOff className="h-4 w-4" />
                                                        Unpromote
                                                    </>
                                                ) : (
                                                    <>
                                                        <Star className="h-4 w-4" />
                                                        Promote
                                                    </>
                                                )}
                                            </button>
                                            <button
                                                onClick={() => handleToggleSkip(project.id, !project.is_skipped_for_promotion)}
                                                disabled={skippingProjectId === project.id}
                                                className="btn btn-secondary btn-sm"
                                                title={project.is_skipped_for_promotion ? "Restore to list" : "Skip (hide from list)"}
                                            >
                                                {skippingProjectId === project.id ? (
                                                    <LoadingSpinner size="sm" />
                                                ) : project.is_skipped_for_promotion ? (
                                                    <ArchiveRestore className="h-4 w-4" />
                                                ) : (
                                                    <Archive className="h-4 w-4" />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}
